import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { 
  ArrowLeft, 
  QrCode, 
  Package, 
  Check, 
  X, 
  AlertCircle,
  Gift,
  Minus,
  Plus,
  RefreshCw,
  User,
  CheckCircle2,
  Camera
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getAccountBalance, deliverProduct } from '../services/accountService';

// Estados del flujo
const STEPS = {
  SCAN: 'scan',
  BALANCE: 'balance',
  SELECT: 'select',
  CONFIRM: 'confirm',
  SUCCESS: 'success'
};

const AccountScanner = () => {
  const navigate = useNavigate();
  
  // Estado del flujo
  const [currentStep, setCurrentStep] = useState(STEPS.SCAN);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estado de cámara
  const [cameraPermission, setCameraPermission] = useState('pending'); // pending, granted, denied
  const [cameraReady, setCameraReady] = useState(false);

  // Verificar permisos de cámara (sin solicitar stream)
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        // Solo verificar si hay cámaras disponibles
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        console.log('[AccountScanner] Cámaras disponibles:', cameras.length);
        
        if (cameras.length === 0) {
          setCameraPermission('denied');
          setError('No se encontró ninguna cámara en el dispositivo.');
          return;
        }
        
        // Marcar como listo - el Scanner manejará los permisos
        setCameraPermission('granted');
        setCameraReady(true);
      } catch (err) {
        console.error('[AccountScanner] Error:', err);
        setCameraPermission('denied');
        setError('Error al acceder a la cámara');
      }
    };

    if (currentStep === STEPS.SCAN) {
      checkCameraPermission();
    }
  }, [currentStep]);
  
  // Datos de la cuenta
  const [accountCode, setAccountCode] = useState('');
  const [accountData, setAccountData] = useState(null);
  const [manualCode, setManualCode] = useState('');
  
  // Productos seleccionados para entrega
  const [selectedProducts, setSelectedProducts] = useState({});
  
  // Resultado de la operación
  const [deliveryResults, setDeliveryResults] = useState([]);

  // Manejar escaneo exitoso
  const handleScan = useCallback(async (result) => {
    if (result && result.length > 0) {
      const code = result[0].rawValue;
      console.log('[AccountScanner] QR escaneado:', code);
      setAccountCode(code);
      setError(null);
      
      // Buscar balance
      setIsLoading(true);
      try {
        const balance = await getAccountBalance(code);
        console.log('[AccountScanner] Balance obtenido:', balance);
        setAccountData(balance);
        setCurrentStep(STEPS.BALANCE);
      } catch (err) {
        console.error('[AccountScanner] Error al obtener balance:', err);
        setError(err.data?.message || err.message || 'Error al buscar la cuenta');
        toast.error('No se pudo encontrar la cuenta');
      } finally {
        setIsLoading(false);
      }
    }
  }, []);

  // Manejar error de escaneo
  const handleScanError = useCallback((err) => {
    console.error('[AccountScanner] Error de escaneo:', err);
    if (err?.name === 'NotAllowedError') {
      setCameraPermission('denied');
      setError('Permiso de cámara denegado');
    }
  }, []);

  // Búsqueda manual por código
  const handleManualSearch = async () => {
    const code = manualCode.trim();
    if (!code) return;
    
    console.log('[AccountScanner] Búsqueda manual:', code);
    setAccountCode(code);
    setError(null);
    setIsLoading(true);
    
    try {
      const balance = await getAccountBalance(code);
      console.log('[AccountScanner] Balance obtenido:', balance);
      setAccountData(balance);
      setManualCode('');
      setCurrentStep(STEPS.BALANCE);
    } catch (err) {
      console.error('[AccountScanner] Error al obtener balance:', err);
      setError(err.data?.message || err.message || 'Cuenta no encontrada');
      toast.error('No se pudo encontrar la cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  // Reintentar permisos de cámara
  const retryCameraPermission = () => {
    setCameraPermission('pending');
    setCameraReady(false);
    setError(null);
    // Pequeño delay para forzar re-render del Scanner
    setTimeout(() => {
      setCameraPermission('granted');
      setCameraReady(true);
    }, 100);
  };

  // Iniciar operación de selección
  const handleStartOperation = () => {
    setSelectedProducts({});
    setCurrentStep(STEPS.SELECT);
  };

  // Agregar/quitar cantidad de producto
  const updateProductQuantity = (productId, change) => {
    const product = accountData?.products?.find(p => p.product_id === productId);
    if (!product) return;

    setSelectedProducts(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, Math.min(product.available, current + change));
      
      if (newQty === 0) {
        const { [productId]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [productId]: newQty };
    });
  };

  // Obtener cantidad seleccionada de un producto
  const getSelectedQuantity = (productId) => {
    return selectedProducts[productId] || 0;
  };

  // Total de productos seleccionados
  const getTotalSelected = () => {
    return Object.values(selectedProducts).reduce((sum, qty) => sum + qty, 0);
  };

  // Confirmar selección
  const handleConfirmSelection = () => {
    if (getTotalSelected() === 0) {
      toast.warning('Selecciona al menos un producto');
      return;
    }
    setCurrentStep(STEPS.CONFIRM);
  };

  // Ejecutar entregas
  const handleDelivery = async () => {
    setIsLoading(true);
    setError(null);
    const results = [];

    try {
      for (const [productId, quantity] of Object.entries(selectedProducts)) {
        if (quantity > 0) {
          const product = accountData.products.find(p => p.product_id === parseInt(productId));
          try {
            const result = await deliverProduct(accountCode, {
              product_id: parseInt(productId),
              quantity: quantity,
              notes: `Entrega desde app móvil`
            });
            results.push({
              success: true,
              product_name: product?.product_name || `Producto ${productId}`,
              quantity,
              remaining: result.data?.remaining_balance
            });
          } catch (err) {
            results.push({
              success: false,
              product_name: product?.product_name || `Producto ${productId}`,
              quantity,
              error: err.data?.message || err.message
            });
          }
        }
      }

      setDeliveryResults(results);
      
      const successCount = results.filter(r => r.success).length;
      if (successCount === results.length) {
        toast.success('¡Entrega completada exitosamente!');
      } else if (successCount > 0) {
        toast.warning('Algunas entregas fallaron');
      } else {
        toast.error('No se pudo completar la entrega');
      }
      
      setCurrentStep(STEPS.SUCCESS);
    } catch (err) {
      console.error('[AccountScanner] Error en entrega:', err);
      setError('Error al procesar la entrega');
      toast.error('Error al procesar la entrega');
    } finally {
      setIsLoading(false);
    }
  };

  // Reiniciar flujo
  const handleReset = () => {
    setCurrentStep(STEPS.SCAN);
    setAccountCode('');
    setAccountData(null);
    setSelectedProducts({});
    setDeliveryResults([]);
    setError(null);
  };

  // Volver al balance
  const handleBackToBalance = () => {
    setSelectedProducts({});
    setCurrentStep(STEPS.BALANCE);
  };

  // Renderizar paso de escaneo
  const renderScanStep = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm bg-gray-800 rounded-2xl overflow-hidden shadow-2xl">
          <div className="p-4 bg-gradient-to-r from-primary-600 to-secondary-600">
            <div className="flex items-center gap-3">
              <QrCode size={28} className="text-white" />
              <div>
                <h2 className="text-white font-bold text-lg">Escanear QR</h2>
                <p className="text-white/70 text-sm">Apunta al código de la cuenta</p>
              </div>
            </div>
          </div>
          
          <div className="relative aspect-square bg-black">
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <RefreshCw size={48} className="text-primary-400 animate-spin mx-auto mb-3" />
                  <p className="text-white">Buscando cuenta...</p>
                </div>
              </div>
            ) : cameraPermission === 'pending' ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <RefreshCw size={48} className="text-primary-400 animate-spin mx-auto mb-3" />
                  <p className="text-white">Solicitando acceso a cámara...</p>
                </div>
              </div>
            ) : cameraPermission === 'denied' ? (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 p-4">
                <div className="text-center">
                  <AlertCircle size={48} className="text-red-400 mx-auto mb-3" />
                  <p className="text-white mb-2">Cámara no disponible</p>
                  <p className="text-gray-400 text-sm mb-4">{error || 'Habilita el permiso de cámara'}</p>
                  <button
                    onClick={retryCameraPermission}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg flex items-center gap-2 mx-auto"
                  >
                    <RefreshCw size={18} />
                    Reintentar
                  </button>
                </div>
              </div>
            ) : cameraReady ? (
              <Scanner
                onScan={handleScan}
                onError={handleScanError}
                formats={['qr_code']}
                scanDelay={100}
                components={{
                  audio: true,
                  torch: true,
                  finder: true,
                  zoom: true,
                }}
                constraints={{
                  facingMode: 'environment',
                  width: { min: 640, ideal: 1280, max: 1920 },
                  height: { min: 480, ideal: 720, max: 1080 },
                  aspectRatio: { ideal: 1 },
                }}
                styles={{
                  container: { 
                    width: '100%', 
                    height: '100%',
                    position: 'relative'
                  },
                  video: { 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <RefreshCw size={48} className="text-primary-400 animate-spin mx-auto mb-3" />
                  <p className="text-white">Iniciando cámara...</p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-900/50 border-t border-red-700">
              <div className="flex items-center gap-2 text-red-300">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-400 underline"
              >
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>

        {/* Búsqueda manual */}
        <div className="mt-4 bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-sm mb-3 text-center">
            ¿No funciona el escáner? Ingresa el código manualmente:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="Código de cuenta"
              className="flex-1 bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-primary-500 focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
            />
            <button
              onClick={handleManualSearch}
              disabled={!manualCode.trim() || isLoading}
              className="px-4 py-3 bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <RefreshCw size={20} className="animate-spin" />
              ) : (
                'Buscar'
              )}
            </button>
          </div>
        </div>

        <p className="text-gray-500 text-center mt-4 text-xs px-4">
          Escanea el código QR o ingresa el código manualmente
        </p>
      </div>
    </div>
  );

  // Renderizar paso de balance
  const renderBalanceStep = () => (
    <div className="flex flex-col h-full">
      {/* Header con info de cuenta */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <User size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-white font-bold text-lg">
              {accountData?.account_name || `Cuenta ${accountCode}`}
            </h2>
            <p className="text-white/70 text-sm">Código: {accountData?.account_code}</p>
          </div>
        </div>
        
        {/* Resumen de balance */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <p className="text-white/60 text-xs">Asignados</p>
            <p className="text-white font-bold text-xl">{accountData?.total_assigned || 0}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2 text-center">
            <p className="text-white/60 text-xs">Entregados</p>
            <p className="text-white font-bold text-xl">{accountData?.total_delivered || 0}</p>
          </div>
          <div className="bg-green-500/30 rounded-lg p-2 text-center">
            <p className="text-green-200 text-xs">Disponibles</p>
            <p className="text-green-300 font-bold text-xl">{accountData?.total_available || 0}</p>
          </div>
        </div>
      </div>

      {/* Lista de productos */}
      <div className="flex-1 overflow-auto p-4">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Gift size={20} className="text-primary-400" />
          Productos Disponibles
        </h3>

        {accountData?.products?.length > 0 ? (
          <div className="space-y-3">
            {accountData.products.map((product) => (
              <div
                key={product.product_id}
                className={`bg-gray-800 rounded-xl p-4 border ${
                  product.available > 0 ? 'border-gray-700' : 'border-gray-800 opacity-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center">
                      <Package size={20} className="text-primary-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{product.product_name}</h4>
                      <div className="flex gap-3 text-xs mt-1">
                        <span className="text-gray-400">
                          Asignado: <span className="text-white">{product.assigned}</span>
                        </span>
                        <span className="text-gray-400">
                          Entregado: <span className="text-white">{product.delivered}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`text-right ${product.available > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                    <p className="text-2xl font-bold">{product.available}</p>
                    <p className="text-xs">disponibles</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Package size={48} className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No hay productos asignados a esta cuenta</p>
          </div>
        )}
      </div>

      {/* Botones de acción */}
      <div className="p-4 bg-gray-800 border-t border-gray-700">
        {accountData?.total_available > 0 ? (
          <button
            onClick={handleStartOperation}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <Gift size={20} />
            Iniciar Entrega
          </button>
        ) : (
          <button
            disabled
            className="w-full py-3 bg-gray-700 text-gray-500 rounded-xl font-semibold cursor-not-allowed"
          >
            Sin productos disponibles
          </button>
        )}
        <button
          onClick={handleReset}
          className="w-full mt-2 py-3 bg-gray-700 text-white rounded-xl font-semibold"
        >
          Escanear otra cuenta
        </button>
      </div>
    </div>
  );

  // Renderizar paso de selección
  const renderSelectStep = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToBalance}
            className="p-2 bg-white/20 rounded-lg"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div>
            <h2 className="text-white font-bold text-lg">Seleccionar Productos</h2>
            <p className="text-white/70 text-sm">{accountData?.account_code}</p>
          </div>
        </div>
      </div>

      {/* Lista de productos para seleccionar */}
      <div className="flex-1 overflow-auto p-4">
        <div className="space-y-3">
          {accountData?.products?.filter(p => p.available > 0).map((product) => (
            <div
              key={product.product_id}
              className={`bg-gray-800 rounded-xl p-4 border transition-all ${
                getSelectedQuantity(product.product_id) > 0 
                  ? 'border-primary-500 bg-primary-900/20' 
                  : 'border-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg flex items-center justify-center">
                    <Package size={20} className="text-primary-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{product.product_name}</h4>
                    <p className="text-green-400 text-sm">
                      {product.available} disponibles
                    </p>
                  </div>
                </div>
                
                {/* Controles de cantidad */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateProductQuantity(product.product_id, -1)}
                    disabled={getSelectedQuantity(product.product_id) === 0}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      getSelectedQuantity(product.product_id) > 0
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-800 text-gray-600'
                    }`}
                  >
                    <Minus size={20} />
                  </button>
                  <span className="w-12 text-center text-white font-bold text-xl">
                    {getSelectedQuantity(product.product_id)}
                  </span>
                  <button
                    onClick={() => updateProductQuantity(product.product_id, 1)}
                    disabled={getSelectedQuantity(product.product_id) >= product.available}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      getSelectedQuantity(product.product_id) < product.available
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-800 text-gray-600'
                    }`}
                  >
                    <Plus size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Resumen y botón de confirmar */}
      <div className="p-4 bg-gray-800 border-t border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-400">Productos seleccionados</span>
          <span className="text-white font-bold text-xl">{getTotalSelected()}</span>
        </div>
        <button
          onClick={handleConfirmSelection}
          disabled={getTotalSelected() === 0}
          className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 ${
            getTotalSelected() > 0
              ? 'bg-primary-600 text-white'
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Check size={20} />
          Continuar
        </button>
      </div>
    </div>
  );

  // Renderizar paso de confirmación
  const renderConfirmStep = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentStep(STEPS.SELECT)}
            className="p-2 bg-white/20 rounded-lg"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div>
            <h2 className="text-white font-bold text-lg">Confirmar Entrega</h2>
            <p className="text-white/70 text-sm">{accountData?.account_code}</p>
          </div>
        </div>
      </div>

      {/* Resumen de entrega */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-gray-800 rounded-xl p-4 mb-4">
          <h3 className="text-white font-semibold mb-3">Productos a entregar:</h3>
          <div className="space-y-2">
            {Object.entries(selectedProducts).map(([productId, quantity]) => {
              const product = accountData?.products?.find(p => p.product_id === parseInt(productId));
              return (
                <div key={productId} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                  <span className="text-white">{product?.product_name || `Producto ${productId}`}</span>
                  <span className="text-primary-400 font-bold">x{quantity}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={24} className="text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-200 font-semibold">¿Confirmar entrega?</p>
              <p className="text-yellow-200/70 text-sm mt-1">
                Esta acción registrará la entrega de {getTotalSelected()} producto(s) al cliente.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="p-4 bg-gray-800 border-t border-gray-700">
        <button
          onClick={handleDelivery}
          disabled={isLoading}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <RefreshCw size={20} className="animate-spin" />
              Procesando...
            </>
          ) : (
            <>
              <CheckCircle2 size={20} />
              Confirmar Entrega
            </>
          )}
        </button>
        <button
          onClick={() => setCurrentStep(STEPS.SELECT)}
          disabled={isLoading}
          className="w-full mt-2 py-3 bg-gray-700 text-white rounded-xl font-semibold"
        >
          Cancelar
        </button>
      </div>
    </div>
  );

  // Renderizar paso de éxito
  const renderSuccessStep = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <CheckCircle2 size={28} className="text-white" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Entrega Completada</h2>
            <p className="text-white/70 text-sm">{accountData?.account_code}</p>
          </div>
        </div>
      </div>

      {/* Resultados */}
      <div className="flex-1 overflow-auto p-4">
        <div className="space-y-3">
          {deliveryResults.map((result, index) => (
            <div
              key={index}
              className={`bg-gray-800 rounded-xl p-4 border ${
                result.success ? 'border-green-700' : 'border-red-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  result.success ? 'bg-green-900' : 'bg-red-900'
                }`}>
                  {result.success ? (
                    <Check size={20} className="text-green-400" />
                  ) : (
                    <X size={20} className="text-red-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-medium">{result.product_name}</h4>
                  <p className={`text-sm ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                    {result.success 
                      ? `${result.quantity} entregado(s) - Quedan: ${result.remaining}`
                      : result.error
                    }
                  </p>
                </div>
                <span className="text-white font-bold">x{result.quantity}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Botones de acción */}
      <div className="p-4 bg-gray-800 border-t border-gray-700">
        <button
          onClick={handleReset}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          <QrCode size={20} />
          Escanear Nueva Cuenta
        </button>
        <button
          onClick={() => navigate(-1)}
          className="w-full mt-2 py-3 bg-gray-700 text-white rounded-xl font-semibold"
        >
          Volver al Menú
        </button>
      </div>
    </div>
  );

  // Renderizar según el paso actual
  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEPS.SCAN:
        return renderScanStep();
      case STEPS.BALANCE:
        return renderBalanceStep();
      case STEPS.SELECT:
        return renderSelectStep();
      case STEPS.CONFIRM:
        return renderConfirmStep();
      case STEPS.SUCCESS:
        return renderSuccessStep();
      default:
        return renderScanStep();
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header fijo */}
      {currentStep === STEPS.SCAN && (
        <div className="bg-gray-800 p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 bg-gray-700 rounded-lg"
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div>
              <h1 className="text-white font-bold text-lg">Entregas por Cuenta</h1>
              <p className="text-gray-400 text-sm">Escanea el QR del cliente</p>
            </div>
          </div>
        </div>
      )}

      {/* Contenido */}
      <div className="flex-1 flex flex-col">
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default AccountScanner;
